/*
 * The v1 DataSphere persona model — see docs/adr/0010-backstage-persona-and-permission-model-v1.md.
 *
 * This module is the single source of truth for who may do what. It is pure
 * TypeScript with no Backstage or React dependency so the frontend, the
 * backend routes and the permission policy all decide from the same map.
 *
 * Const objects rather than enums: the backend loads this file as TypeScript
 * at runtime, and Node's type stripping only removes erasable syntax. An enum
 * emits runtime code and fails with ERR_UNSUPPORTED_TYPESCRIPT_SYNTAX.
 */

// Values double as the datasphere.personaGroups config keys, so they are
// camelCase. ADR-0010 (amended 2026-10-19) split the old Requestor persona
// into SpokeRequestor and SpokeAdmin.
export const Persona = {
  Admin: 'admin',
  SpokeRequestor: 'spokeRequestor',
  SpokeAdmin: 'spokeAdmin',
  Approver: 'approver',
  Consumer: 'consumer',
} as const;

export type PersonaValue = (typeof Persona)[keyof typeof Persona];

export const Capability = {
  /** The DataSphere landing page and published documentation. */
  LandingRead: 'landing.read',
  /** Hubs & spokes overview and the software catalog. */
  CatalogRead: 'catalog.read',
  /** Run the spoke-request scaffolder template. */
  SpokeRequestRun: 'workflow.spoke-request.run',
  /** Run the catalog-schema-request scaffolder template. */
  CatalogRequestRun: 'workflow.catalog-request.run',
  /** Run the user-groups-request scaffolder template. */
  UserGroupsRequestRun: 'workflow.user-groups-request.run',
  /** See one's own rows in the requests table. */
  RequestReadOwn: 'request.read.own',
  /** See every user's rows in the requests table. */
  RequestReadAll: 'request.read.all',
  /** Approve or reject a request. */
  RequestApprove: 'request.approve',
  /** Administration pages and overrides. */
  AdminAccess: 'admin.access',
} as const;

export type CapabilityValue = (typeof Capability)[keyof typeof Capability];

export const CAPABILITIES_BY_PERSONA: Readonly<
  Record<PersonaValue, readonly CapabilityValue[]>
> = {
  [Persona.Admin]: [
    Capability.LandingRead,
    Capability.CatalogRead,
    Capability.SpokeRequestRun,
    Capability.CatalogRequestRun,
    Capability.UserGroupsRequestRun,
    Capability.RequestReadOwn,
    Capability.RequestReadAll,
    Capability.RequestApprove,
    Capability.AdminAccess,
  ],
  // Neither spoke persona may approve, not even requests for their own spoke.
  [Persona.SpokeRequestor]: [
    Capability.LandingRead,
    Capability.CatalogRead,
    Capability.SpokeRequestRun,
    Capability.RequestReadOwn,
  ],
  [Persona.SpokeAdmin]: [
    Capability.LandingRead,
    Capability.CatalogRead,
    Capability.CatalogRequestRun,
    Capability.UserGroupsRequestRun,
    Capability.RequestReadOwn,
  ],
  [Persona.Approver]: [
    Capability.LandingRead,
    Capability.CatalogRead,
    Capability.RequestReadOwn,
    Capability.RequestReadAll,
    Capability.RequestApprove,
  ],
  [Persona.Consumer]: [Capability.LandingRead],
};

/** One capability per scaffolder template. Any of them opens "Services". */
export const WORKFLOW_CAPABILITIES: readonly CapabilityValue[] = [
  Capability.SpokeRequestRun,
  Capability.CatalogRequestRun,
  Capability.UserGroupsRequestRun,
];

/**
 * Template annotation that names the capability a user needs to see and run
 * that template, for example `workflow.spoke-request.run`. A template without
 * it, or with an unknown value, is seen and run by Admins only.
 */
export const TEMPLATE_RUN_CAPABILITY_ANNOTATION =
  'datasphere.howden.com/run-capability';

export const canRunAnyWorkflow = (
  capabilities: readonly CapabilityValue[],
): boolean =>
  WORKFLOW_CAPABILITIES.some(capability => capabilities.includes(capability));

/**
 * Backstage ownership entity refs carried in the signed-in user's token, one
 * per persona. The sign-in resolver adds these after reading the user's Entra
 * group membership; nothing else in the system may mint them.
 */
export const PERSONA_GROUP_REFS: Readonly<Record<PersonaValue, string>> = {
  [Persona.Admin]: 'group:default/datasphere-admin',
  [Persona.SpokeRequestor]: 'group:default/datasphere-spoke-requestor',
  [Persona.SpokeAdmin]: 'group:default/datasphere-spoke-admin',
  [Persona.Approver]: 'group:default/datasphere-approver',
  [Persona.Consumer]: 'group:default/datasphere-consumer',
};

const PERSONA_BY_GROUP_REF = new Map<string, PersonaValue>(
  Object.entries(PERSONA_GROUP_REFS).map(([persona, ref]) => [
    ref,
    persona as PersonaValue,
  ]),
);

const PERSONA_ORDER: readonly PersonaValue[] = [
  Persona.Admin,
  Persona.SpokeRequestor,
  Persona.SpokeAdmin,
  Persona.Approver,
  Persona.Consumer,
];

export const isPersona = (value: unknown): value is PersonaValue =>
  typeof value === 'string' && PERSONA_ORDER.includes(value as PersonaValue);

export const personaFromGroupRef = (ref: string): PersonaValue | undefined =>
  PERSONA_BY_GROUP_REF.get(ref.trim().toLowerCase());

/**
 * Personas carried by a set of ownership entity refs. A user holding none of
 * the persona groups is a Consumer: the portal's landing page and docs are
 * readable by anyone the tenant lets sign in.
 */
export const personasFromOwnershipRefs = (
  refs: readonly string[] | undefined,
): PersonaValue[] => {
  const found = new Set<PersonaValue>();

  for (const ref of refs ?? []) {
    const persona = personaFromGroupRef(ref);
    if (persona) {
      found.add(persona);
    }
  }

  if (found.size === 0) {
    return [Persona.Consumer];
  }

  return PERSONA_ORDER.filter(persona => found.has(persona));
};

/** Capabilities are additive: a user gets the union of their personas'. */
export const capabilitiesFor = (
  personas: readonly PersonaValue[],
): CapabilityValue[] => {
  const granted = new Set<CapabilityValue>();

  for (const persona of personas) {
    for (const capability of CAPABILITIES_BY_PERSONA[persona] ?? []) {
      granted.add(capability);
    }
  }

  return Object.values(Capability).filter(capability =>
    granted.has(capability),
  );
};

export const hasCapability = (
  personas: readonly PersonaValue[],
  capability: CapabilityValue,
): boolean => capabilitiesFor(personas).includes(capability);

const SPOKE_ADMIN_REF_PREFIX = 'group:default/spoke-admins-';

/**
 * Ownership entity ref for one spoke the user administers. The sign-in
 * resolver adds one per GP_GDL_<SPOKE>_SPOKE_ADMINS group that the user is in
 * and that is nested into the Spoke Admin portal group (ADR-0010).
 */
export const spokeAdminRef = (spoke: string): string =>
  `${SPOKE_ADMIN_REF_PREFIX}${spoke.trim().toLowerCase()}`;

/** Spokes the user administers, read back from their ownership refs. */
export const spokesFromOwnershipRefs = (
  refs: readonly string[] | undefined,
): string[] =>
  Array.from(
    new Set(
      (refs ?? [])
        .map(ref => ref.trim().toLowerCase())
        .filter(ref => ref.startsWith(SPOKE_ADMIN_REF_PREFIX))
        .map(ref => ref.slice(SPOKE_ADMIN_REF_PREFIX.length)),
    ),
  ).sort();

/**
 * Whether the user may request catalogs and schemas for a spoke. An Admin may
 * for any spoke; anyone else only for a spoke in `spokeAdminOf`.
 */
export const canAdministerSpoke = (
  personas: readonly PersonaValue[],
  spokeAdminOf: readonly string[],
  spoke: string,
): boolean =>
  personas.includes(Persona.Admin) ||
  spokeAdminOf.includes(spoke.trim().toLowerCase());

export type PersonaSession = {
  personas: PersonaValue[];
  capabilities: CapabilityValue[];
  /** Spokes the user administers. Empty for an Admin, who is not scoped. */
  spokeAdminOf: string[];
};

/** Local-dev sign-in that bypasses SSO; the backend refuses it outside NODE_ENV=development. */
export const DEV_PERSONA_PROVIDER_ID = 'dev-persona';
export const DEV_PERSONA_HEADER = 'x-datasphere-dev-persona';

export const devPersonaEmail = (persona: PersonaValue): string =>
  `dev.${persona}@datasphere.local`;

const EMAIL_REF_PREFIX = 'user:default/email-';

/**
 * A deterministic ownership entity ref for an email address. Backstage's user
 * info service exposes only `userEntityRef` and `ownershipEntityRefs`, so this
 * is how the caller's email survives the sign-in token and lets the backend
 * match a request row's `requestor_email` to the caller.
 */
export const emailOwnershipRef = (email: string): string =>
  `${EMAIL_REF_PREFIX}${email
    .trim()
    .toLowerCase()
    .replace(/@/g, '_at_')
    .replace(/[^a-z0-9._-]/g, '-')}`;

export const ownershipRefsIncludeEmail = (
  refs: readonly string[] | undefined,
  email: string | undefined | null,
): boolean => {
  if (!email) {
    return false;
  }

  const target = emailOwnershipRef(email);
  return (refs ?? []).some(ref => ref.trim().toLowerCase() === target);
};

/**
 * Most people have two Entra accounts: a normal one, which has a mailbox, and
 * an admin one, which is the only kind that gets Databricks access. The spoke
 * request needs a normal account for the Spoke Owner, the Spoke Admins and
 * the Acceptance Owner, and an admin account for the three data personas.
 */
export const AccountKind = {
  Admin: 'admin',
  Normal: 'normal',
} as const;

export type AccountKindValue = (typeof AccountKind)[keyof typeof AccountKind];

export const isAccountKind = (value: unknown): value is AccountKindValue =>
  Object.values(AccountKind).includes(value as AccountKindValue);

// No Entra attribute marks an admin account, so the kind comes from the naming
// convention: initials, "admin" and a number (cfadmin5@; the oldest have no
// number, meadmin@), plus the older and third-party forms nlam.admin@,
// rmadmn10@ and SDubey.adm@.
const ADMIN_ACCOUNT_NAME = /admin|admn?\d*$/i;
// "Matt Tyer - Admin Account", "Adam Barker- IT Admin Account". Catches the
// admin accounts whose name does not follow the convention.
const ADMIN_DISPLAY_NAME = /admin account/i;

export const accountKindOf = (user: {
  email: string;
  displayName?: string;
}): AccountKindValue => {
  const accountName = user.email.trim().split('@')[0];
  return ADMIN_ACCOUNT_NAME.test(accountName) ||
    ADMIN_DISPLAY_NAME.test(user.displayName ?? '')
    ? AccountKind.Admin
    : AccountKind.Normal;
};

/** Why `user` cannot hold `role`, or undefined when the account kind is right. */
export const accountKindRefusal = (
  user: { email: string; displayName?: string },
  required: AccountKindValue,
  role: string,
): string | undefined => {
  if (accountKindOf(user) === required) {
    return undefined;
  }

  return required === AccountKind.Normal
    ? `${user.email} is an admin account. ${role} must be a normal account, the one with a mailbox.`
    : `${user.email} is not an admin account. ${role} must be an admin account (for example xyadmin1@howdengroup.com): only admin accounts get Databricks access.`;
};

/** Inverse of {@link emailOwnershipRef}, for recording who took an action. */
export const emailFromOwnershipRefs = (
  refs: readonly string[] | undefined,
): string | undefined => {
  for (const ref of refs ?? []) {
    const normalised = ref.trim().toLowerCase();
    if (normalised.startsWith(EMAIL_REF_PREFIX)) {
      return normalised.slice(EMAIL_REF_PREFIX.length).replace(/_at_/g, '@');
    }
  }

  return undefined;
};
